import {FC, useEffect} from 'react';
import {Box, Button, Flex, Stack} from '@chakra-ui/react';
import {api, IAgentTask, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Modal, Textarea} from 'ui-kit';

interface IProps {
  isOpen: boolean;
  task: IAgentTask | null;
  onClose: () => void;
}

interface IAgentTaskForm {
  prompt: string;
}

const AgentTaskEditorModalFC: FC<IProps> = ({isOpen, task, onClose}) => {
  const updateMutation = api.agentTasks.useUpdateAgentTaskMutation();

  const {t} = useT();

  const {
    control,
    handleSubmit,
    reset,
    formState: {isValid}
  } = useForm<IAgentTaskForm>({
    mode: 'onChange',
    defaultValues: {prompt: ''}
  });

  // Re-seed on open and clear state on close, so a fresh editor never shows stale text.
  useEffect(() => {
    reset({prompt: isOpen ? (task?.prompt ?? '') : ''});
  }, [isOpen, task, reset]);

  const onSubmit = (data: IAgentTaskForm) => {
    if (!task) return;

    updateMutation.mutate({id: task.id, prompt: data.prompt.trim()}, {onSuccess: onClose});
  };

  return (
    <Modal isOpen={isOpen} title={t('agentTasks.editTask')} maxW={'520px'} onClose={onClose}>
      <Stack gap={'0'} p={'16px 24px 0'}>
        <Box mb={'6px'} color={'{colors.textSecondary}'}>
          {t('agentTasks.fields.prompt')}
        </Box>
        <Controller
          name={'prompt'}
          control={control}
          rules={{validate: (v) => !!v.trim() || t('agentTasks.validation.promptRequired')}}
          render={({field}) => (
            <Textarea
              rows={6}
              size={'lg'}
              autoresize={false}
              value={field.value}
              placeholder={t('agentTasks.placeholders.prompt')}
              onChange={field.onChange}
            />
          )}
        />

        <Flex gap={'12px'} justifyContent={'flex-end'} p={'12px 0 8px 0'}>
          <Button
            size={'lg'}
            variant={'subtle'}
            disabled={updateMutation.isPending}
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            size={'lg'}
            variant={'solid'}
            loading={updateMutation.isPending}
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            {t('agentTasks.actions.save')}
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};

export const AgentTaskEditorModal = AgentTaskEditorModalFC;
